/**
 * DrillTimer - Live training drill timer with referee whistle
 * 
 * Features:
 * - Add drills with name + countdown duration (minutes/seconds)
 * - Large countdown display with Start/Pause/Reset
 * - Pulsing animation when timer is running
 * - Realistic referee whistle via Web Audio API
 * - Total active drill time accumulator
 * - Saves drill data to backend
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Play, Pause, RotateCcw, Plus, Trash2, Timer, Clock,
  Volume2, VolumeX, ChevronUp, ChevronDown, Zap, StopCircle, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ───────────────── Web Audio Whistle ─────────────────
// Models a pea-whistle (Fox 40 style): tube resonance + pea trill + air turbulence
function playWhistle(audioCtxRef, type = 'start') {
  try {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const now = ctx.currentTime;

    // Blast config per type
    const blasts = type === 'end' ? 3 : type === 'pause' ? 2 : 1;
    const blastLen = type === 'end' ? 0.5 : type === 'pause' ? 0.25 : 0.8;
    const gap = 0.1;

    for (let b = 0; b < blasts; b++) {
      const t0 = now + b * (blastLen + gap);
      const attack = 0.015;
      const release = 0.04;

      // ── Master bus for this blast ──
      const bus = ctx.createGain();
      bus.gain.setValueAtTime(0, t0);
      bus.gain.linearRampToValueAtTime(0.7, t0 + attack);
      bus.gain.setValueAtTime(0.7, t0 + blastLen - release);
      bus.gain.linearRampToValueAtTime(0, t0 + blastLen);
      bus.connect(ctx.destination);

      // ── Pea trill LFO (rapid amplitude modulation ~28 Hz) ──
      const trillLfo = ctx.createOscillator();
      const trillGain = ctx.createGain();
      trillLfo.type = 'sine';
      trillLfo.frequency.setValueAtTime(28, t0);
      // Slight randomness in trill speed
      trillLfo.frequency.linearRampToValueAtTime(32, t0 + blastLen * 0.5);
      trillLfo.frequency.linearRampToValueAtTime(26, t0 + blastLen);
      trillGain.gain.setValueAtTime(0.3, t0); // trill depth (0 = none, 1 = full)
      trillLfo.connect(trillGain);

      // ── Primary whistle tone with trill ──
      // Fundamental ~2900 Hz + harmonics for tube resonance
      const fundamentals = [
        { freq: 2900, vol: 0.22, type: 'sine' },
        { freq: 3400, vol: 0.12, type: 'sine' },   // 1st overtone
        { freq: 5800, vol: 0.04, type: 'sine' },   // 2nd harmonic
        { freq: 2400, vol: 0.06, type: 'triangle' }, // sub-resonance warmth
      ];

      fundamentals.forEach(({ freq, vol, type: oscType }) => {
        const osc = ctx.createOscillator();
        osc.type = oscType;
        // Slight pitch drift (breath pressure variation)
        osc.frequency.setValueAtTime(freq * 0.98, t0);
        osc.frequency.linearRampToValueAtTime(freq, t0 + 0.05);
        osc.frequency.setValueAtTime(freq, t0 + blastLen * 0.7);
        osc.frequency.linearRampToValueAtTime(freq * 0.97, t0 + blastLen);

        // Frequency vibrato from pea trill LFO
        const freqMod = ctx.createGain();
        freqMod.gain.setValueAtTime(freq * 0.015, t0); // ±1.5% pitch wobble
        trillLfo.connect(freqMod);
        freqMod.connect(osc.frequency);

        const oscGain = ctx.createGain();
        oscGain.gain.setValueAtTime(vol, t0);

        // Amplitude trill (pea modulation)
        trillGain.connect(oscGain.gain);

        // Tube resonance filter
        const resonance = ctx.createBiquadFilter();
        resonance.type = 'bandpass';
        resonance.frequency.setValueAtTime(freq, t0);
        resonance.Q.setValueAtTime(8, t0);

        osc.connect(resonance);
        resonance.connect(oscGain);
        oscGain.connect(bus);
        osc.start(t0);
        osc.stop(t0 + blastLen);
      });

      trillLfo.start(t0);
      trillLfo.stop(t0 + blastLen);

      // ── Air turbulence noise (breathy layer) ──
      const noiseDur = blastLen;
      const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * noiseDur, ctx.sampleRate);
      const noiseData = noiseBuf.getChannelData(0);
      for (let i = 0; i < noiseData.length; i++) {
        noiseData[i] = (Math.random() * 2 - 1);
      }
      const noiseSrc = ctx.createBufferSource();
      noiseSrc.buffer = noiseBuf;

      // Shape noise to whistle frequency range
      const noiseBpf = ctx.createBiquadFilter();
      noiseBpf.type = 'bandpass';
      noiseBpf.frequency.setValueAtTime(3200, t0);
      noiseBpf.Q.setValueAtTime(2.5, t0);

      // Second filter for high-end hiss
      const hissBpf = ctx.createBiquadFilter();
      hissBpf.type = 'highpass';
      hissBpf.frequency.setValueAtTime(4500, t0);
      hissBpf.Q.setValueAtTime(1, t0);

      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0, t0);
      noiseGain.gain.linearRampToValueAtTime(0.08, t0 + attack);
      noiseGain.gain.setValueAtTime(0.08, t0 + blastLen - release);
      noiseGain.gain.linearRampToValueAtTime(0, t0 + blastLen);

      const hissGain = ctx.createGain();
      hissGain.gain.setValueAtTime(0, t0);
      hissGain.gain.linearRampToValueAtTime(0.03, t0 + attack);
      hissGain.gain.linearRampToValueAtTime(0, t0 + blastLen);

      noiseSrc.connect(noiseBpf);
      noiseBpf.connect(noiseGain);
      noiseGain.connect(bus);

      const noiseSrc2 = ctx.createBufferSource();
      noiseSrc2.buffer = noiseBuf;
      noiseSrc2.connect(hissBpf);
      hissBpf.connect(hissGain);
      hissGain.connect(bus);

      noiseSrc.start(t0);
      noiseSrc.stop(t0 + blastLen);
      noiseSrc2.start(t0);
      noiseSrc2.stop(t0 + blastLen);
    }
  } catch (e) {
    console.warn('Whistle playback failed:', e);
  }
}

// ───────────────── Helpers ─────────────────
const formatTime = (totalSec) => {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const newDrill = () => ({
  id: `drill_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  name: '',
  target_seconds: 300, // 5 min default
  elapsed_seconds: 0,
  status: 'idle', // idle | running | paused | completed
});

const DEFAULT_PRESETS = [
  { name: 'Warm-up', minutes: 5, seconds: 0 },
  { name: 'Passing Drill', minutes: 10, seconds: 0 },
  { name: 'Shooting Practice', minutes: 8, seconds: 0 },
  { name: 'Small-Sided Game', minutes: 15, seconds: 0 },
  { name: 'Cool Down', minutes: 5, seconds: 0 },
];

// ───────────────── Component ─────────────────
export function DrillTimer({ training, onDrillsUpdate, presets: userPresets }) {
  const [drills, setDrills] = useState(() => {
    if (training?.drills?.length) {
      return training.drills.map(d => ({
        ...d,
        status: d.status === 'running' ? 'paused' : (d.status || 'idle'),
      }));
    }
    return [];
  });
  const [whistleEnabled, setWhistleEnabled] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const presetPickerRef = useRef(null);

  const availablePresets = userPresets?.length ? userPresets : DEFAULT_PRESETS;

  // Close preset picker on outside click
  useEffect(() => {
    if (!showPresetPicker) return;
    const handler = (e) => {
      if (presetPickerRef.current && !presetPickerRef.current.contains(e.target)) {
        setShowPresetPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPresetPicker]);

  // Tick: decrement active timers every second
  useEffect(() => {
    const hasRunning = drills.some(d => d.status === 'running');
    if (!hasRunning) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setDrills(prev => prev.map(d => {
        if (d.status !== 'running') return d;
        const next = d.elapsed_seconds + 1;
        if (next >= d.target_seconds) {
          // Drill done
          if (whistleEnabled) playWhistle(audioCtxRef, 'end');
          toast.success(`"${d.name || 'Drill'}" complete!`);
          return { ...d, elapsed_seconds: d.target_seconds, status: 'completed' };
        }
        return { ...d, elapsed_seconds: next };
      }));
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [drills, whistleEnabled]);

  // Notify parent of drills changes — use ref to avoid re-render loop
  const onDrillsUpdateRef = useRef(onDrillsUpdate);
  onDrillsUpdateRef.current = onDrillsUpdate;
  const prevDrillsJson = useRef(JSON.stringify(drills));
  useEffect(() => {
    const json = JSON.stringify(drills);
    if (json !== prevDrillsJson.current) {
      prevDrillsJson.current = json;
      if (onDrillsUpdateRef.current) onDrillsUpdateRef.current(drills);
    }
  }, [drills]);

  // ── Drill Actions ──
  const addDrill = () => setDrills(prev => [...prev, newDrill()]);

  const loadPreset = (preset) => {
    const totalSec = (preset.minutes || 0) * 60 + (preset.seconds || 0);
    setDrills(prev => [...prev, {
      ...newDrill(),
      name: preset.name,
      target_seconds: totalSec,
    }]);
    setShowPresetPicker(false);
  };

  const removeDrill = (id) => setDrills(prev => prev.filter(d => d.id !== id));

  const updateDrillField = (id, field, value) => {
    setDrills(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  const startDrill = (id) => {
    // Pause any other running drill first
    setDrills(prev => prev.map(d => {
      if (d.id === id) {
        if (whistleEnabled) playWhistle(audioCtxRef, 'start');
        return { ...d, status: 'running' };
      }
      if (d.status === 'running') return { ...d, status: 'paused' };
      return d;
    }));
  };

  const pauseDrill = (id) => {
    if (whistleEnabled) playWhistle(audioCtxRef, 'pause');
    setDrills(prev => prev.map(d =>
      d.id === id ? { ...d, status: 'paused' } : d
    ));
  };

  const stopDrill = (id) => {
    if (!window.confirm('Are you sure you want to stop this drill?')) return;
    if (whistleEnabled) playWhistle(audioCtxRef, 'end');
    setDrills(prev => prev.map(d =>
      d.id === id ? { ...d, status: 'completed' } : d
    ));
  };

  const resetDrill = (id) => {
    setDrills(prev => prev.map(d =>
      d.id === id ? { ...d, elapsed_seconds: 0, status: 'idle' } : d
    ));
  };

  const saveDrills = async () => {
    if (!training?.id) return;
    setSaving(true);
    try {
      await axios.put(`${API}/trainings/${training.id}/drills`, drills);
      toast.success('Drills saved');
    } catch {
      toast.error('Failed to save drills');
    } finally {
      setSaving(false);
    }
  };

  // ── Computed ──
  const totalActiveSeconds = drills.reduce((sum, d) => sum + d.elapsed_seconds, 0);
  const hasRunning = drills.some(d => d.status === 'running');

  return (
    <div className="mt-3" data-testid="drill-timer-section">
      {/* Header toggle */}
      <button
        className="w-full flex items-center justify-between px-3 py-2 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
        onClick={() => setExpanded(e => !e)}
        data-testid="drill-timer-toggle"
      >
        <div className="flex items-center gap-2">
          <Timer className="w-4 h-4 text-emerald-700" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-emerald-800">
            Drill Timer
          </span>
          {drills.length > 0 && (
            <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">
              {drills.length} drill{drills.length !== 1 ? 's' : ''}
            </span>
          )}
          {totalActiveSeconds > 0 && (
            <span className="text-xs text-emerald-600 font-mono ml-1" data-testid="total-drill-time">
              Total: {formatTime(totalActiveSeconds)}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-emerald-600" />
        ) : (
          <ChevronDown className="w-4 h-4 text-emerald-600" />
        )}
      </button>

      {expanded && (
        <div className="mt-2 space-y-3 animate-in slide-in-from-top-2 duration-200">
          {/* Controls row */}
          <div className="flex items-center justify-between px-1 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-lg text-xs gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={addDrill}
                data-testid="add-drill-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add Drill
              </Button>
              <div className="relative" ref={presetPickerRef}>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-lg text-xs gap-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                  onClick={() => setShowPresetPicker(p => !p)}
                  data-testid="load-preset-btn"
                >
                  <Zap className="w-3.5 h-3.5" /> Preset
                </Button>
                {showPresetPicker && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[200px] py-1 animate-in fade-in slide-in-from-top-1 duration-150" data-testid="preset-picker-dropdown">
                    {availablePresets.map((preset, i) => (
                      <button
                        key={i}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 transition-colors flex items-center justify-between gap-3"
                        onClick={() => loadPreset(preset)}
                        data-testid={`preset-option-${i}`}
                      >
                        <span className="font-medium text-gray-800 truncate">{preset.name}</span>
                        <span className="text-xs text-gray-400 font-mono flex-shrink-0">
                          {preset.minutes || 0}:{String(preset.seconds || 0).padStart(2, '0')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => setWhistleEnabled(w => !w)}
                title={whistleEnabled ? 'Mute whistle' : 'Enable whistle'}
                data-testid="whistle-toggle"
              >
                {whistleEnabled ? (
                  <Volume2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <VolumeX className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {drills.length > 0 && (
                <Button
                  size="sm"
                  className="rounded-lg text-xs bg-emerald-700 hover:bg-emerald-800 text-white gap-1"
                  onClick={saveDrills}
                  disabled={saving}
                  data-testid="save-drills-btn"
                >
                  {saving ? 'Saving...' : 'Save Drills'}
                </Button>
              )}
            </div>
          </div>

          {/* Drills list */}
          {drills.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm" data-testid="no-drills-msg">
              <Timer className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>No drills added yet</p>
              <p className="text-xs mt-1">Tap "Add Drill" to start timing</p>
            </div>
          ) : (
            <div className="space-y-3">
              {drills.map((drill) => (
                <DrillCard
                  key={drill.id}
                  drill={drill}
                  onStart={() => startDrill(drill.id)}
                  onPause={() => pauseDrill(drill.id)}
                  onStop={() => stopDrill(drill.id)}
                  onReset={() => resetDrill(drill.id)}
                  onRemove={() => removeDrill(drill.id)}
                  onUpdateField={(field, val) => updateDrillField(drill.id, field, val)}
                />
              ))}
            </div>
          )}

          {/* Total summary */}
          {drills.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-lg" data-testid="drill-summary">
              <span className="text-xs font-medium text-gray-600">Total Active Time</span>
              <span className="text-lg font-bold font-mono text-emerald-700">{formatTime(totalActiveSeconds)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────── DrillCard ─────────────────
function DrillCard({ drill, onStart, onPause, onStop, onReset, onRemove, onUpdateField }) {
  const remaining = Math.max(0, drill.target_seconds - drill.elapsed_seconds);
  const progress = drill.target_seconds > 0
    ? (drill.elapsed_seconds / drill.target_seconds) * 100
    : 0;
  const isRunning = drill.status === 'running';
  const isCompleted = drill.status === 'completed';
  const isIdle = drill.status === 'idle';

  return (
    <div
      className={`rounded-xl border transition-all duration-300 ${
        isRunning
          ? 'border-emerald-400 bg-emerald-50 shadow-md shadow-emerald-100'
          : isCompleted
          ? 'border-gray-300 bg-gray-50'
          : 'border-gray-200 bg-white'
      }`}
      data-testid={`drill-card-${drill.id}`}
    >
      {/* Drill header */}
      <div className="flex items-center gap-2 px-3 pt-3">
        {isCompleted && (
          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" strokeWidth={2} />
        )}
        <Input
          value={drill.name}
          onChange={(e) => onUpdateField('name', e.target.value)}
          placeholder="Drill name..."
          className={`h-8 text-sm rounded-lg border-gray-200 font-medium flex-1 ${isCompleted ? 'text-gray-400' : ''}`}
          disabled={isRunning || isCompleted}
          data-testid={`drill-name-${drill.id}`}
        />
        {isCompleted && (
          <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full flex-shrink-0" data-testid={`drill-complete-badge-${drill.id}`}>
            Complete
          </span>
        )}
        <button
          onClick={onRemove}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
          disabled={isRunning}
          data-testid={`remove-drill-${drill.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Duration setter (only when idle) */}
      {isIdle && (
        <div className="flex items-center justify-center gap-3 px-3 pt-2 pb-1" data-testid={`drill-duration-setter-${drill.id}`}>
          <button
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 flex items-center justify-center text-gray-600 text-xl font-bold transition-colors"
            onClick={() => onUpdateField('target_seconds', Math.max(60, drill.target_seconds - 60))}
            data-testid={`drill-min-down-${drill.id}`}
          >
            &minus;
          </button>
          <div className="flex flex-col items-center">
            <span className="text-3xl font-black text-gray-800 font-mono tabular-nums" data-testid={`drill-min-${drill.id}`}>
              {Math.floor(drill.target_seconds / 60)}
            </span>
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">min</span>
          </div>
          <button
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 flex items-center justify-center text-gray-600 text-xl font-bold transition-colors"
            onClick={() => onUpdateField('target_seconds', Math.min(99 * 60, drill.target_seconds + 60))}
            data-testid={`drill-min-up-${drill.id}`}
          >
            +
          </button>
        </div>
      )}

      {/* Countdown / Completed display */}
      <div className="px-3 py-3 flex flex-col items-center">
        <div className={`relative ${isRunning ? 'animate-pulse' : ''}`}>
          <span
            className={`font-mono text-4xl sm:text-5xl font-black tracking-wider ${
              isRunning
                ? 'text-emerald-700'
                : isCompleted
                ? 'text-gray-400'
                : 'text-gray-800'
            }`}
            data-testid={`drill-countdown-${drill.id}`}
          >
            {isCompleted ? formatTime(drill.elapsed_seconds) : formatTime(remaining)}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ${
              isCompleted ? 'bg-emerald-400' : 'bg-emerald-500'
            }`}
            style={{ width: `${isCompleted ? 100 : Math.min(100, progress)}%` }}
            data-testid={`drill-progress-${drill.id}`}
          />
        </div>

        {/* Label under timer */}
        <span className="text-xs text-gray-400 mt-1 font-mono" data-testid={`drill-elapsed-${drill.id}`}>
          {isCompleted
            ? `${formatTime(drill.elapsed_seconds)} of ${formatTime(drill.target_seconds)}`
            : `${formatTime(drill.elapsed_seconds)} elapsed`
          }
        </span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-2 px-3 pb-3">
        {isRunning ? (
          <>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full h-10 w-10 p-0 border-amber-300 text-amber-600 hover:bg-amber-50"
              onClick={onPause}
              data-testid={`pause-drill-${drill.id}`}
            >
              <Pause className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full h-10 w-10 p-0 border-red-300 text-red-500 hover:bg-red-50"
              onClick={onStop}
              data-testid={`stop-drill-${drill.id}`}
            >
              <StopCircle className="w-4 h-4" />
            </Button>
          </>
        ) : isCompleted ? (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full h-10 w-10 p-0 border-gray-200 text-gray-500 hover:bg-gray-50"
            onClick={onReset}
            data-testid={`reset-drill-${drill.id}`}
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              className="rounded-full h-10 w-10 p-0 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={onStart}
              disabled={drill.target_seconds === 0}
              data-testid={`start-drill-${drill.id}`}
            >
              <Play className="w-4 h-4 ml-0.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full h-10 w-10 p-0 border-gray-200 text-gray-500 hover:bg-gray-50"
              onClick={onReset}
              disabled={drill.elapsed_seconds === 0}
              data-testid={`reset-drill-${drill.id}`}
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
